import { isInUIC } from '../../../utils/common';
import config from 'src/config';
import { BannerType } from '../../../utils/enums';

const UPGRADE_MODAL_STATUS_KEY = 'casUpgradeModalStatus';

export const checkForShowUpgradeModal = () => {
  if (isInUIC()) return false;

  // { lastClosedTimestamp: 1743064624000 }
  const ls = localStorage.getItem(UPGRADE_MODAL_STATUS_KEY);

  if (!ls) return true;

  try {
    const lastClosedDate = new Date(
      JSON.parse(ls).lastClosedTimestamp as number
    );
    const now = new Date();
    // check if the same date
    // if same, not show
    return now.toLocaleDateString() !== lastClosedDate.toLocaleDateString();
  } catch {
    localStorage.removeItem(UPGRADE_MODAL_STATUS_KEY);
    return true;
  }
};

export const updateUpgradeBannerClosedTime = () => {
  localStorage.setItem(
    UPGRADE_MODAL_STATUS_KEY,
    JSON.stringify({
      lastClosedTimestamp: Date.now(),
    })
  );
};

const globalSite = config.ui.siteName.startsWith('prod')
  ? 'prod_wu'
  : config.ui.siteName;
const v1CurrentSite = (config.ui.siteInfo.v1 as any)[globalSite];

const CONNECT_TO_V1_URL = `${v1CurrentSite}/#/admin/products?create_instance_type=sca`;
const UPDATE_TO_V1_URL = `${v1CurrentSite}/#${config.ui.uicPrefix}/welcome`;
const V1_FOR_EVERYONE_URL = 'https://activation.trendmicro.com/g1';
export const gotoConnectToV1 = () => window.open(UPDATE_TO_V1_URL, '_blank');

export const gotoSolutionUpgrade = () =>
  window.open(CONNECT_TO_V1_URL, '_blank');

export const gotoFlywheelSignup = (notificationContent: string) => {
  let flywheelUrl = '';
  try {
    flywheelUrl = (
      new DOMParser()
        .parseFromString(notificationContent, 'text/html')
        .querySelector("a[name='notificationButtonLink']") as HTMLAnchorElement
    ).href;
  } catch (e) {
    console.error('get flywheelUrl error', e);
  }

  if (!flywheelUrl) {
    console.error('flywheelUrl not exist');
    return;
  }

  window.open(flywheelUrl, '_blank');
};

export const gotoV1ForEveryone = () =>
  window.open(V1_FOR_EVERYONE_URL, '_blank');

/**
 * Determines the banner type based on promotion banner API response data
 * @param promotionBannerData - The data from GlobalApi.getPromotionBanner() response
 * @returns BannerType enum value
 */
export const getBannerTypeFromData = (
  promotionBannerData?: any
): BannerType => {
  if (!promotionBannerData || !promotionBannerData.name) {
    return BannerType.Flywheel; // Default to non-V1 customer
  }

  const name = promotionBannerData.name;
  if (name === BannerType.ConnectedSolution) {
    return BannerType.ConnectedSolution;
  } else if (name === BannerType.SolutionUpgrade) {
    return BannerType.SolutionUpgrade;
  } else {
    return BannerType.Flywheel;
  }
};

export enum PromotionPopupWindowPhaseEnum {
  euUkPhase1 = 1, // EU & UK Phase 1
  euUkPhase3 = 2, // EU & UK Phase 3
  globalPhase1 = 3, // Global Site Phase 1
  globalPhase2 = 4, // Global Site Phase 2
  globalPhase3 = 5, // Global Site Phase 3
}

/**
 * Get deadline date by phase (ISO format)
 */
export const getDeadlineDateByPhase = (
  phase: PromotionPopupWindowPhaseEnum
): string => {
  const deadlineMap: Record<PromotionPopupWindowPhaseEnum, string> = {
    [PromotionPopupWindowPhaseEnum.euUkPhase1]: '2026-02-28',
    [PromotionPopupWindowPhaseEnum.euUkPhase3]: '2025-08-31',
    [PromotionPopupWindowPhaseEnum.globalPhase1]:
      config.ui.upgradeDeadline?.globalPhase1 || '2026-02-28',
    [PromotionPopupWindowPhaseEnum.globalPhase2]: '2026-06-30', // TBD
    [PromotionPopupWindowPhaseEnum.globalPhase3]: '2026-09-30', // TBD
  };
  return deadlineMap[phase] || '2026-02-28';
};

/**
 * Format deadline date based on locale
 * @example en: "February 28, 2026", ja: "2026年2月28日"
 */
export const formatDeadlineDate = (dateStr: string, locale: string): string => {
  // Use 'T00:00:00' to ensure local-time parsing (avoid UTC midnight shift in UTC+ timezones)
  const date = new Date(dateStr + 'T00:00:00');
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
};

/**
 * Check if should show PhaseOneUpgradeModal (closable modal)
 */
export const isPhaseOneModal = (
  phase: PromotionPopupWindowPhaseEnum
): boolean => {
  return [
    PromotionPopupWindowPhaseEnum.euUkPhase1,
    PromotionPopupWindowPhaseEnum.globalPhase1,
    PromotionPopupWindowPhaseEnum.globalPhase2,
    PromotionPopupWindowPhaseEnum.globalPhase3,
  ].includes(phase);
};

/**
 * Check if should show UpgradeModal (non-closable modal)
 */
export const isPhaseThreeModal = (
  phase: PromotionPopupWindowPhaseEnum
): boolean => {
  return [PromotionPopupWindowPhaseEnum.euUkPhase3].includes(phase);
};

/**
 * Check if deadline has passed for a given phase
 * @returns true if current date is on or after the deadline
 */
export const isDeadlinePassed = (
  phase: PromotionPopupWindowPhaseEnum
): boolean => {
  const deadlineStr = getDeadlineDateByPhase(phase);
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return today >= deadlineStr;
};
